import {
  generateId,
  getCurrentUserRecord,
  getDatabase,
  updateDatabase
} from '../data/localDataStore';

class ProductFlagsService {
  async getUserProductFlags(sessionId = null) {
    try {
      const currentUser = getCurrentUserRecord();
      const database = getDatabase();

      let flags = [];
      if (currentUser) {
        flags = database.productCartFlags.filter((flag) => flag.user_id === currentUser.id);
      } else if (sessionId) {
        flags = database.productCartFlags.filter((flag) => flag.session_id === sessionId);
      } else {
        return { success: false, error: 'No user or session ID provided' };
      }

      const flagsMap = {};
      flags.forEach((flag) => {
        flagsMap[flag.product_sku] = flag?.is_flagged;
      });

      return { success: true, data: flagsMap };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  async validateProductExists(productSku) {
    try {
      const database = getDatabase();
      return {
        success: true,
        exists: database.products.some((product) => product.sku === productSku)
      };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  async validateMultipleProducts(productSkus) {
    try {
      const database = getDatabase();
      const productSet = new Set(database.products.map((product) => product.sku));
      const missingProducts = productSkus
        .filter((sku) => !productSet.has(sku))
        .map((sku) => ({ missing_sku: sku }));
      const existingSKUs = productSkus.filter((sku) => productSet.has(sku));

      return { 
        success: true, 
        existingSKUs, 
        missingProducts,
        allExist: missingProducts?.length === 0,
        detailedReport: missingProducts
      };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  async getValidationReport() {
    try {
      const database = getDatabase();
      const productSet = new Set(database.products.map((product) => product.sku));
      const orphanedFlags = database.productCartFlags.filter(
        (flag) => !productSet.has(flag.product_sku)
      );

      return {
        success: true,
        data: {
          total_products: database.products.length,
          active_products: database.products.filter((product) => product.is_active !== false).length,
          total_flags: database.productCartFlags.length,
          orphaned_flags: orphanedFlags.length
        }
      };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  async diagnoseProductIssues(productSkus = null, checkAllFlags = false) {
    try {
      const database = getDatabase();
      const targetSkus = checkAllFlags
        ? database.productCartFlags.map((flag) => flag.product_sku)
        : productSkus || [];
      const productSet = new Set(database.products.map((product) => product.sku));

      const data = [...new Set(targetSkus)].map((sku) => ({
        sku,
        exists: productSet.has(sku),
        issue: productSet.has(sku) ? null : 'Prodotto mancante nel database locale'
      }));

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  async cleanupOrphanedFlags() {
    try {
      const database = getDatabase();
      const productSet = new Set(database.products.map((product) => product.sku));
      const deletedCount = database.productCartFlags.filter(
        (flag) => !productSet.has(flag.product_sku)
      ).length;

      updateDatabase((draft) => {
        draft.productCartFlags = draft.productCartFlags.filter((flag) =>
          productSet.has(flag.product_sku)
        );
      });

      return {
        success: true,
        data: {
          deleted_count: deletedCount
        }
      };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  async setProductFlag(productSku, isFlagged, sessionId = null) {
    try {
      const currentUser = getCurrentUserRecord();
      const database = getDatabase();
      const productExists = database.products.some((product) => product.sku === productSku);

      if (!productExists) {
        return {
          success: false,
          error: `Il prodotto "${productSku}" non esiste nel sistema locale.`
        };
      }

      const flagData = {
        id: generateId('flag'),
        product_sku: productSku,
        is_flagged: isFlagged,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (currentUser) {
        flagData.user_id = currentUser.id;
        flagData.session_id = null;
      } else if (sessionId) {
        flagData.session_id = sessionId;
        flagData.user_id = null;
      } else {
        return { success: false, error: 'No user or session ID provided' };
      }

      let savedFlag = null;

      updateDatabase((draft) => {
        const existingFlag = draft.productCartFlags.find((flag) => {
          const sameOwner = currentUser
            ? flag.user_id === currentUser.id
            : flag.session_id === sessionId;

          return sameOwner && flag.product_sku === productSku;
        });

        if (existingFlag) {
          existingFlag.is_flagged = isFlagged;
          existingFlag.updated_at = new Date().toISOString();
          savedFlag = existingFlag;
          return;
        }

        draft.productCartFlags.push(flagData);
        savedFlag = flagData;
      });

      return {
        success: true,
        data: savedFlag,
        previousFlagStatus: null
      };
    } catch (error) {
      return { success: false, error: `Errore imprevisto: ${error?.message}` };
    }
  }

  async removeProductFlag(productSku, sessionId = null) {
    try {
      const currentUser = getCurrentUserRecord();

      if (!currentUser && !sessionId) {
        return { success: false, error: 'No user or session ID provided' };
      }

      updateDatabase((draft) => {
        draft.productCartFlags = draft.productCartFlags.filter((flag) => {
          if (flag.product_sku !== productSku) {
            return true;
          }

          if (currentUser) {
            return flag.user_id !== currentUser.id;
          }

          return flag.session_id !== sessionId;
        });
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  async clearAllFlags(sessionId = null) {
    try {
      const currentUser = getCurrentUserRecord();

      if (!currentUser && !sessionId) {
        return { success: false, error: 'No user or session ID provided' };
      }

      updateDatabase((draft) => {
        draft.productCartFlags = draft.productCartFlags.filter((flag) => {
          if (currentUser) {
            return flag.user_id !== currentUser.id;
          }

          return flag.session_id !== sessionId;
        });
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  async getCartProductFlags(productSkus, sessionId = null) {
    try {
      const currentUser = getCurrentUserRecord();
      const database = getDatabase();

      const flags = database.productCartFlags.filter((flag) => {
        if (!productSkus.includes(flag.product_sku)) {
          return false;
        }

        if (currentUser) {
          return flag.user_id === currentUser.id;
        }

        if (sessionId) {
          return flag.session_id === sessionId;
        }

        return false;
      });

      const flagsMap = {};
      flags.forEach((flag) => {
        flagsMap[flag.product_sku] = flag.is_flagged;
      });

      return { success: true, data: flagsMap };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  async validateProductsExist(productSkus) {
    try {
      const database = getDatabase();
      const existingSKUs = database.products
        .filter((product) => productSkus.includes(product.sku))
        .map((product) => product.sku);
      const missingSKUs = productSkus.filter((sku) => !existingSKUs.includes(sku));

      return {
        success: true,
        existingSKUs,
        missingSKUs,
        allExist: missingSKUs.length === 0
      };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  async getAvailableProducts() {
    try {
      const database = getDatabase();
      const data = database.products
        .filter((product) => product.is_active !== false)
        .sort((left, right) => (left.name || '').localeCompare(right.name || ''));

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  generateSessionId() {
    return 'session_' + Math.random()?.toString(36)?.substring(2, 15) + Math.random()?.toString(36)?.substring(2, 15);
  }
}

export const productFlagsService = new ProductFlagsService();
